import { useEffect, useRef } from 'react'
import type { DataTableInstance, DataTableRowData, DataTableSortingState } from '../types'
import { getCanRankRows } from '../utils/row.utils'

export const useTableEffects = <TData extends DataTableRowData>(
  table: DataTableInstance<TData>,
) => {
  const {
    getPrePaginationRowModel,
    getState,
    options: { enablePagination, rowCount },
  } = table
  const { globalFilter, isLoading, pagination, showSkeletons, sorting } = getState()

  const totalRowCount = rowCount ?? getPrePaginationRowModel().rows.length

  const initialBodyHeight = useRef<string>()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      initialBodyHeight.current = document.body.style.height
    }
  }, [])

  // if page index is out of bounds, set it to the last page
  useEffect(() => {
    if (!enablePagination || isLoading || showSkeletons) return
    const { pageIndex, pageSize } = pagination
    const firstVisibleRowIndex = pageIndex * pageSize
    if (firstVisibleRowIndex >= totalRowCount) {
      table.setPageIndex(Math.ceil(totalRowCount / pageSize) - 1)
    }
  }, [totalRowCount])

  // turn off sort when global filter is looking for ranked results
  const appliedSort = useRef<DataTableSortingState>(sorting)
  useEffect(() => {
    if (sorting.length) {
      appliedSort.current = sorting
    }
  }, [sorting])

  useEffect(() => {
    if (!getCanRankRows(table)) return
    if (globalFilter) {
      table.setSorting([])
    } else {
      table.setSorting(() => appliedSort.current || [])
    }
  }, [globalFilter])
}
